import { linkOptions, useNavigate, useSearch } from '@tanstack/react-router';

import { CenteredPagination } from '../cdl/Pagination/CenteredPagination';
import { AdvancedError } from '../common/Error/AdvancedError';
import { formatDate } from '../common/helpers/formatDate.helper';
import { translate } from '../common/helpers/translate.helper';
import { useRole } from '../common/hooks/useRole';
import { IconHeadset } from '../common/icons/cdl/Headset';
import { LoadingIndicator } from '../common/LoadingIndicator/LoadingIndicator';
import { PortListing } from '../common/PortListing/PortListing';
import { TableEmptyState } from '../common/TableEmptyState/TableEmptyState';
import { Tabs } from '../cdl/Tabs/Tabs';

import { TableBuilder } from '../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../cdl/TableBuilder/TableBuilderColumn';
import { formatCompanyName } from '../common/helpers/formatCompanyName.helper';
import { Box } from '../common/ui/Box';
import { ProductContext } from '../../types/ProductContext';
import { AgentTableActions } from './components/AgentTableActions';
import { useAgentPagination } from './useAgentPagination';
import { useAgentPaginationTotals } from './useAgentPaginationTotals';

export const AgentsOverviewTable = () => {
    const role = useRole();

    const search = useSearch({ strict: false });
    const navigate = useNavigate();

    const active = search.tab === 'active';

    const { data: agentTotals } = useAgentPaginationTotals({
        searchQuery: search.searchQuery,
        customerIds: search.customerIds,
        portIds: search.portIds,
    });

    const { data, pageTotal, isPending, isError } = useAgentPagination({
        page: search.page,
        searchQuery: search.searchQuery,
        customerIds: search.customerIds,
        portIds: search.portIds,
        active,
    });

    const totals = {
        active: agentTotals?.active,
        archived: agentTotals?.archived,
    };

    if (isPending) {
        return (
            <div>
                <Tabs
                    selectedContext={search.tab}
                    onSelect={(tab) => navigate({ search: (previousSearch) => ({ ...previousSearch, tab, page: 0 }) })}
                    totals={totals}
                />
                <LoadingIndicator />
            </div>
        );
    }

    if (isError) {
        return (
            <div>
                <Tabs
                    selectedContext={search.tab}
                    onSelect={(tab) => navigate({ search: (previousSearch) => ({ ...previousSearch, tab, page: 0 }) })}
                    totals={totals}
                />
                <AdvancedError />
            </div>
        );
    }

    if (!data.content?.length) {
        return (
            <div>
                <Tabs
                    selectedContext={search.tab}
                    onSelect={(tab) => navigate({ search: (previousSearch) => ({ ...previousSearch, tab, page: 0 }) })}
                    totals={totals}
                />
                <TableEmptyState Icon={IconHeadset} text={translate('agents.emptyList')} />
            </div>
        );
    }

    return (
        <div>
            <Tabs
                selectedContext={search.tab}
                onSelect={(tab) => navigate({ search: (previousSearch) => ({ ...previousSearch, tab, page: 0 }) })}
                totals={totals}
            />
            <TableBuilder
                data={data.content}
                rowLink={(agent) => linkOptions({ to: '/agents/$id', params: { id: agent.id } })}
                grow={false}
            >
                <TableBuilderColumn header={translate('agents.name')} width="25%" truncate>
                    {(agent) => agent.name}
                </TableBuilderColumn>
                {role.isSuperAdmin() || !role.isOneCompanyUser() ? (
                    <TableBuilderColumn header={translate('agents.company')} width="20%" truncate>
                        {(agent) =>
                            agent.customer
                                ? formatCompanyName({
                                      company: agent.customer,
                                  })
                                : ''
                        }
                    </TableBuilderColumn>
                ) : null}
                <TableBuilderColumn header={translate('agents.ports')} width="20%">
                    {(agent) => (agent.ports.length ? <PortListing ports={agent.ports} /> : '')}
                </TableBuilderColumn>
                <TableBuilderColumn header={translate('agents.dateCreated')} width="10%">
                    {(agent) => formatDate({ date: agent.dateCreated })}
                </TableBuilderColumn>
                <TableBuilderColumn header={translate('agents.lastEdit')} width="10%">
                    {(agent) => formatDate({ date: agent.dateUpdated })}
                </TableBuilderColumn>
                {role.hasAnyCompanyWriteRights() ? (
                    <TableBuilderColumn header="" width="5%" interactive>
                        {(agent) =>
                            role.hasWriteRights(agent.customerId) ? (
                                <Box display="flex" flexWrap="nowrap" justifyContent="flex-end">
                                    <AgentTableActions agent={agent} />
                                </Box>
                            ) : (
                                ''
                            )
                        }
                    </TableBuilderColumn>
                ) : null}
            </TableBuilder>
            <CenteredPagination
                pageTotal={pageTotal}
                currentPage={search.page}
                onPageChange={(page) => navigate({ search: (previousSearch) => ({ ...previousSearch, page }) })}
            />
        </div>
    );
};
